import { z } from "zod";
import { Version } from "./concurrent.js";

// ---------------------------------------------------------------------------
// 顧客コード・仕入先コードと取引先検索（#288、docs/101 1a §3・§4）。
//   コードは任意の英数字とハイフン・アンダーバー 1〜20 文字（全角は半角に直す・空欄は「無し」）。
//   会社の中で欄ごとに一意（大文字小文字は区別しない＝DB の一意索引 upper(…)）。紙には印字しない。
//   取引先検索は右からのドロアー（スマホは全画面）＝画面の部品 components/PartnerPickerDrawer.tsx 1つ。
//   並びは DB の fn_partner_search（コード前方一致 → 名称・略称・フリガナ → 電話 → 担当者）
// ---------------------------------------------------------------------------

/** 画面に出す文（題・見出し・ボタンは名詞だけ。docs/10 §5.1.2）。{customer}{vendor} は会社の言葉（画面は t()） */
export const PARTNER_PICKER_MSG = {
  title: "取引先検索",
  /** 検索窓の下の薄い字（placeholder は置かない） */
  hint: "名称・フリガナ・コード・電話・担当者",
  recent: "最近",
  frequent: "よく使う",
  newPartner: "新規登録",
  change: "変更",
  contact: "担当者",
  unset: "未設定",
  /** 索引の「未設定」 */
  unsetShort: "未",
  count: (n: number): string => `${n}件`,
  /** よく使う＝直近12か月の書類数 */
  docCount: (n: number): string => `${n}件`,
  docCountTitle: "直近12か月の書類数",
  lastDeal: (md: string): string => `最終 ${md}`,
  branchCount: (n: number): string => `支店 ${n}`,
  none: "該当なし",
  noPartner: "{customer}の登録はありません",
  /** 新規登録の欄・ボタン */
  register: "登録",
  cancel: "取消",
  close: "閉じる",
  name: "名称",
  kana: "フリガナ",
  customerCode: "顧客コード",
  vendorCode: "仕入先コード",
  branchCode: "支店コード",
  /** 仕入先コードに付ける接頭辞（取込の選択肢） */
  vendorCodePrefix: "仕入先コードの接頭辞",
} as const;

export const PARTNER_CODE_MSG = {
  shape: "半角英数字とハイフン・アンダーバーで20文字までです",
  /** 重複（409 PARTNER_CODE_DUP・BRANCH_CODE_DUP）：相手の名前を入れて返す */
  duplicate: (name: string): string => `このコードは ${name} に使われています`,
} as const;

/** コードの形（DB の CHECK と同じ） */
export const PARTNER_CODE_RE = /^[A-Za-z0-9_-]{1,20}$/;

/** 打ったコードを揃える：NFKC（全角 → 半角）・前後の空白なし（DB の fn_partner_code_norm と同じ）。空なら "" */
export const partnerCodeOf = (s: string): string => s.normalize("NFKC").trim();

/** コードの欄（空＝無し。全角は半角に直す） */
export const PartnerCode = z
  .string()
  .transform(partnerCodeOf)
  .pipe(z.union([z.literal(""), z.string().regex(PARTNER_CODE_RE, PARTNER_CODE_MSG.shape)]));

/**
 * コードの自然順（2 → 10 → A-1）：数字の並びは数として、ほかは大文字小文字を区別せずに比べる。空は最後
 */
export function compareCodes(a: string, b: string): number {
  if (!a || !b) return a ? -1 : b ? 1 : 0;
  const ta = a.toUpperCase().match(/\d+|\D+/g) ?? [];
  const tb = b.toUpperCase().match(/\d+|\D+/g) ?? [];
  for (let i = 0; i < Math.min(ta.length, tb.length); i++) {
    const x = ta[i]!;
    const y = tb[i]!;
    const nx = /^\d/.test(x);
    const ny = /^\d/.test(y);
    if (nx && ny) {
      const d = Number(x) - Number(y);
      if (d !== 0) return d;
      if (x.length !== y.length) return x.length - y.length;
    } else if (nx !== ny) {
      return nx ? -1 : 1;
    } else if (x !== y) {
      return x < y ? -1 : 1;
    }
  }
  return ta.length - tb.length;
}

// ---------------------------------------------------------------------------
// ローマ字 → ひらがな（画面が直して API に kana で送る。ibak → いば）
// ---------------------------------------------------------------------------
const ROMA: Record<string, string> = {
  a: "あ", i: "い", u: "う", e: "え", o: "お",
  ka: "か", ki: "き", ku: "く", ke: "け", ko: "こ", ga: "が", gi: "ぎ", gu: "ぐ", ge: "げ", go: "ご",
  sa: "さ", si: "し", shi: "し", su: "す", se: "せ", so: "そ", za: "ざ", zi: "じ", ji: "じ", zu: "ず", ze: "ぜ", zo: "ぞ",
  ta: "た", ti: "ち", chi: "ち", tu: "つ", tsu: "つ", te: "て", to: "と", da: "だ", di: "ぢ", du: "づ", de: "で", do: "ど",
  na: "な", ni: "に", nu: "ぬ", ne: "ね", no: "の",
  ha: "は", hi: "ひ", hu: "ふ", fu: "ふ", he: "へ", ho: "ほ", ba: "ば", bi: "び", bu: "ぶ", be: "べ", bo: "ぼ", pa: "ぱ", pi: "ぴ", pu: "ぷ", pe: "ぺ", po: "ぽ",
  ma: "ま", mi: "み", mu: "む", me: "め", mo: "も", ya: "や", yu: "ゆ", yo: "よ",
  ra: "ら", ri: "り", ru: "る", re: "れ", ro: "ろ", la: "ら", li: "り", lu: "る", le: "れ", lo: "ろ",
  wa: "わ", wo: "を", nn: "ん", "n'": "ん", vu: "ゔ",
  kya: "きゃ", kyu: "きゅ", kyo: "きょ", gya: "ぎゃ", gyu: "ぎゅ", gyo: "ぎょ",
  sha: "しゃ", shu: "しゅ", sho: "しょ", sya: "しゃ", syu: "しゅ", syo: "しょ", ja: "じゃ", ju: "じゅ", jo: "じょ", jya: "じゃ", jyu: "じゅ", jyo: "じょ", zya: "じゃ", zyu: "じゅ", zyo: "じょ",
  cha: "ちゃ", chu: "ちゅ", cho: "ちょ", tya: "ちゃ", tyu: "ちゅ", tyo: "ちょ",
  nya: "にゃ", nyu: "にゅ", nyo: "にょ", hya: "ひゃ", hyu: "ひゅ", hyo: "ひょ", bya: "びゃ", byu: "びゅ", byo: "びょ", pya: "ぴゃ", pyu: "ぴゅ", pyo: "ぴょ",
  mya: "みゃ", myu: "みゅ", myo: "みょ", rya: "りゃ", ryu: "りゅ", ryo: "りょ",
  fa: "ふぁ", fi: "ふぃ", fe: "ふぇ", fo: "ふぉ", she: "しぇ", je: "じぇ", che: "ちぇ", thi: "てぃ", dhi: "でぃ",
  xa: "ぁ", xi: "ぃ", xu: "ぅ", xe: "ぇ", xo: "ぉ", xtu: "っ", xtsu: "っ", xya: "ゃ", xyu: "ゅ", xyo: "ょ", "-": "ー",
};
const ROMA_MAX = 4;

/**
 * ローマ字をひらがなに（aibakkusu → あいばっくす・ibak → いば）。終わりの書きかけの子音（k・n など）は捨てる（途中まで打った分でも当たる）。
 * 英字・ハイフン・アポストロフィ以外が混ざる・ひらがなにならない・2 文字未満は null（コードの検索と分けるため、英数字のときは API に kana を送らない）
 */
export function romajiToKana(input: string): string | null {
  const s = input.normalize("NFKC").trim().toLowerCase();
  if (s.length < 2 || !/^[a-z'-]+$/.test(s)) return null;
  let out = "";
  let i = 0;
  while (i < s.length) {
    const c = s[i]!;
    const next = s[i + 1];
    // 促音（kk・tt・ss…）：同じ子音が2つ続けば「っ」（n は除く）
    if (next && c === next && /[bcdfghjklmpqrstvwxyz]/.test(c) && c !== "n") {
      out += "っ";
      i += 1;
      continue;
    }
    // nn の次が母音なら「ん」＋な行（konnichiwa → こんにちわ）
    if (c === "n" && next === "n" && /[aiueo]/.test(s[i + 2] ?? "")) {
      out += "ん";
      i += 1;
      continue;
    }
    // n の次が母音・y でなければ「ん」
    if (c === "n" && next && !/[aiueoyn']/.test(next)) {
      out += "ん";
      i += 1;
      continue;
    }
    let hit = "";
    let len = 0;
    for (let l = Math.min(ROMA_MAX, s.length - i); l >= 1; l--) {
      const k = s.slice(i, i + l);
      if (ROMA[k]) {
        hit = ROMA[k]!;
        len = l;
        break;
      }
    }
    if (!len) {
      // 書きかけ（終わりの子音）は捨てる。途中で読めない字があれば null
      if (/^[bcdfghjklmnpqrstvwxyz']+$/.test(s.slice(i))) break;
      return null;
    }
    out += hit;
    i += len;
  }
  return out || null;
}

// ---------------------------------------------------------------------------
// 索引（あ〜わ・未設定）：フリガナの頭の字の行
// ---------------------------------------------------------------------------
export const KANA_ROWS = ["あ", "か", "さ", "た", "な", "は", "ま", "や", "ら", "わ"] as const;
export type KanaRow = (typeof KANA_ROWS)[number] | "未";
const ROW_OF: Array<[KanaRow, string]> = [
  ["あ", "ぁあぃいぅうぇえぉおゔ"],
  ["か", "かがきぎくぐけげこごゕゖ"],
  ["さ", "さざしじすずせぜそぞ"],
  ["た", "ただちぢっつづてでとど"],
  ["な", "なにぬねの"],
  ["は", "はばぱひびぴふぶぷへべぺほぼぽ"],
  ["ま", "まみむめも"],
  ["や", "ゃやゅゆょよ"],
  ["ら", "らりるれろ"],
  ["わ", "ゎわゐゑをん"],
];
/** フリガナ → 索引の行（カタカナ・半角カナもひらがなとして。かなでなければ「未」） */
export function kanaRowOf(kana: string | null | undefined): KanaRow {
  const s = (kana ?? "").normalize("NFKC").trim();
  const c = s[0];
  if (!c) return "未";
  const code = c.charCodeAt(0);
  const h = code >= 0x30a1 && code <= 0x30f6 ? String.fromCharCode(code - 0x60) : c;
  for (const [row, chars] of ROW_OF) if (chars.includes(h)) return row;
  return "未";
}

// ---------------------------------------------------------------------------
// API の形
// ---------------------------------------------------------------------------
/** 取引先検索の役割：customer＝顧客（顧客・両方）／vendor＝仕入先（仕入先・両方）／all */
export const PartnerRole = z.enum(["customer", "vendor", "all"]);
export type PartnerRole = z.infer<typeof PartnerRole>;

export const PartnerSearchContact = z.object({
  id: z.string().uuid(),
  name: z.string(),
  branchId: z.string().uuid().nullable(),
  branchName: z.string(),
});
export type PartnerSearchContact = z.infer<typeof PartnerSearchContact>;

/** 候補の1行（GET /partners/search・/partners/recent） */
export const PartnerSearchHit = z.object({
  id: z.string().uuid(),
  name: z.string(),
  shortName: z.string(),
  displayName: z.string(),
  nameKana: z.string(),
  partnerType: z.enum(["client", "vendor", "both"]),
  honorific: z.string(),
  customerCode: z.string(),
  vendorCode: z.string(),
  branchCount: z.number().int(),
  /** 最終取引日（書類・見積依頼を作った日。無ければ null） */
  lastDealOn: z.string().nullable(),
  /** 1＝コード／2＝名称／3＝電話／4＝担当者／9＝何も打たない（フリガナ順） */
  rank: z.number().int(),
  /** 当たった担当者（rank 4 のとき） */
  contacts: z.array(PartnerSearchContact),
  /** 最近の行：最後の書類の支店・担当者（押すと3つが一緒に入る）。ほかの行は null */
  branch: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  contact: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  /** よく使う：直近12か月の書類数 */
  docCount: z.number().int().optional(),
});
export type PartnerSearchHit = z.infer<typeof PartnerSearchHit>;

export const PartnerSearchResult = z.object({
  items: z.array(PartnerSearchHit),
  /** 何も打たない一覧が上限で切れた */
  more: z.boolean(),
});
export type PartnerSearchResult = z.infer<typeof PartnerSearchResult>;

export const PartnerRecent = z.object({
  /** 自分が最近使った取引先（5件） */
  recent: z.array(PartnerSearchHit),
  /** 会社でよく使う取引先（直近12か月の書類数の上位 10件） */
  frequent: z.array(PartnerSearchHit),
});
export type PartnerRecent = z.infer<typeof PartnerRecent>;

export const PARTNER_SEARCH_LIMITS = {
  /** 打ったときの候補 */
  hits: 30,
  /** 何も打たないときのフリガナ順の一覧 */
  list: 500,
  recent: 5,
  frequent: 10,
  /** 打った文字（これより長いものは切る） */
  query: 60,
} as const;

/** 支店コードだけ直す（PATCH /partners/:id/branches/:bid） */
export const PartnerBranchCodeUpdate = z.object({
  branchCode: PartnerCode,
  /** 版（#202）。省けば見ない */
  version: Version.optional(),
});
export type PartnerBranchCodeUpdate = z.infer<typeof PartnerBranchCodeUpdate>;
